import React, { useMemo, useState, useEffect } from "react";
import availableIcons from "./icons";
import "./IconBackground.css";

const IconBackground = () => {
  // State for how many icons to render based on viewport size
  const [iconCount, setIconCount] = useState(0);

  // Function to calculate required icon count
  const calculateIconCount = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    // Ensure we're generating enough icons to fill the screen properly
    // but adjust slightly for mobile to improve performance
    const iconSize = 30;
    const spacingFactor = width < 768 ? 1.2 : 1; // Slightly reduce density on mobile
    
    const columns = Math.ceil((width * 1.5) / (iconSize * spacingFactor));
    const rows = Math.ceil((height * 1.5) / (iconSize * spacingFactor));
    
    // Add extra buffer rows and columns
    return (rows + 4) * (columns + 4);
  };

  // Update iconCount on mount and on window resize
  useEffect(() => {
    const updateIconCount = () => {
      setIconCount(calculateIconCount());
    };
    updateIconCount();
    window.addEventListener("resize", updateIconCount);
    return () => window.removeEventListener("resize", updateIconCount);
  }, []);

  // Generate icons array dynamically based on iconCount.
  const icons = useMemo(() => {
    // Filter out any icons with undefined components first
    const validIcons = availableIcons.filter(icon => typeof icon.component === 'function');
    
    return Array.from({ length: iconCount }, (_, index) => {
      // Only use valid icons to prevent rendering errors
      const randomIndex = Math.floor(Math.random() * validIcons.length);
      const iconObj = validIcons[randomIndex];
      const Icon = iconObj.component;
      return { Icon, key: index };
    });
  }, [iconCount]);

  // Memoize glow classes
  const glowClasses = useMemo(() => ['glow-1', 'glow-2', 'glow-3', 'glow-4'], []);

  // State for glowing items; each item: { index, className }
  const [glowingItems, setGlowingItems] = useState([]);

  // Configurable durations (in milliseconds)
  const glowDuration = 2000; // How long icons glow
  const pauseDuration = 1500; // How long to wait after the glow

  useEffect(() => {
    const totalCycle = glowDuration + pauseDuration;
    const interval = setInterval(() => {
      // Check if we have icons to avoid issues
      if (icons.length === 0) return;

      // Pick as many random indices as there are glow classes
      const count = Math.min(glowClasses.length, icons.length);
      let indices = [];
      while (indices.length < count) {
        let randomIndex = Math.floor(Math.random() * icons.length);
        if (!indices.includes(randomIndex)) {
          indices.push(randomIndex);
        }
      }
      const newGlowingItems = indices.map((index, i) => ({
        index,
        className: glowClasses[i],
      }));
      setGlowingItems(newGlowingItems);

      // Remove the glow after glowDuration
      setTimeout(() => {
        setGlowingItems([]);
      }, glowDuration);
    }, totalCycle);

    return () => clearInterval(interval);
  }, [icons.length, glowDuration, pauseDuration, glowClasses]);

  // Using the same base styles as the original to maintain consistency
  const baseIconStyle = {
    fontSize: '30px',
    color: '#8b868680',
    margin: '10px',
    transition: 'color 0.8s ease, filter 0.8s ease'
  };

  return (
    <div className="icon-background">
      <div className="icon-grid">
        {icons.map((iconItem, index) => {
          const { Icon, key } = iconItem;
          // Make sure Icon is a valid component before rendering
          if (!Icon) return null;
          
          // Apply a glow class if it's part of glowingItems
          const glowingItem = glowingItems.find((item) => item.index === index);
          
          return (
            <Icon
              key={key}
              className={`background-icon ${glowingItem ? glowingItem.className : ""}`}
              style={baseIconStyle}
              size={30}
            />
          );
        })}
      </div>
    </div>
  );
};

export default IconBackground;